import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

/**
 * The numbered step an integration page is built out of.
 *
 * Extracted from the Jira import page so the Clockify one is visibly its
 * sibling: same numbering, same badges, same "blocked steps say why instead of
 * rendering a body you cannot act on" rule. An import screen that looks like a
 * stranger reads like a different product.
 */
export type StepState = 'todo' | 'in_progress' | 'done';

const STEP_BADGES: Record<StepState, { label: string; className: string }> = {
  todo: { label: 'To do', className: 'text-muted-foreground border-border bg-muted/40' },
  in_progress: {
    label: 'In progress',
    className: 'text-blue-500 border-blue-500/30 bg-blue-500/5',
  },
  done: { label: 'Done', className: 'text-green-600 border-green-600/30 bg-green-600/5' },
};

export function StepCard({
  step,
  title,
  state,
  blockedReason,
  description,
  action,
  children,
}: {
  step: number;
  title: string;
  state: StepState;
  /** Present = the admin cannot act on this step yet, and this is why. */
  blockedReason?: string;
  description?: string;
  action?: ReactNode;
  children?: ReactNode;
}) {
  const blocked = blockedReason !== undefined;
  const badge = STEP_BADGES[state];

  return (
    <Card className={cn(blocked && 'opacity-60')}>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold tabular-nums text-muted-foreground">
              {step}
            </span>
            {title}
          </CardTitle>
          <Badge variant="outline" className={cn('shrink-0 px-1.5 text-[10px]', badge.className)}>
            {badge.label}
          </Badge>
          {action && <div className="shrink-0 sm:ml-auto">{action}</div>}
        </div>
        {(blocked || description) && (
          <CardDescription>{blockedReason ?? description}</CardDescription>
        )}
      </CardHeader>
      {!blocked && children && <CardContent className="space-y-3">{children}</CardContent>}
    </Card>
  );
}
